import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { ulid } from 'ulid'
import { UploadError, useUploadFile } from './useUploadFile'

export const MAX_CHAT_ATTACHMENTS = 5
const UPLOAD_CONCURRENCY = 3
const MAX_BYTES = 10 * 1024 * 1024 // 10MB

export interface PendingAttachment {
  localId: string
  file: File
  previewUrl: string
  attachmentId?: string
  publicUrl?: string
  status: 'uploading' | 'ready' | 'failed'
  error?: string
  abort: AbortController
}

interface UsePendingAttachmentsResult {
  pending: PendingAttachment[]
  addFiles: (files: File[]) => void
  removeAt: (localId: string) => void
  clear: () => void
  hasUploading: boolean
  ready: PendingAttachment[]
}

export function usePendingAttachments(): UsePendingAttachmentsResult {
  const [pending, setPending] = useState<PendingAttachment[]>([])
  const uploadFile = useUploadFile()

  // Track active in-flight count for the simple concurrency cap
  const activeCountRef = useRef(0)
  const queueRef = useRef<PendingAttachment[]>([])

  const startUpload = useCallback(
    async (att: PendingAttachment) => {
      // Note: activeCountRef.current is incremented synchronously by the
      // caller (enqueueUpload) before this fires, so a tight loop of N
      // addFiles() calls still respects UPLOAD_CONCURRENCY.
      try {
        const result = await uploadFile(att.file, {
          maxBytes: MAX_BYTES,
          signal: att.abort.signal,
        })
        setPending(prev =>
          prev.map(p =>
            p.localId === att.localId
              ? {
                  ...p,
                  status: 'ready',
                  attachmentId: result.attachmentId,
                  publicUrl: result.publicUrl,
                }
              : p
          )
        )
      } catch (err) {
        const reason = err instanceof UploadError ? err.reason : 'upload_failed'
        if (reason === 'aborted') {
          // Already removed from state by removeAt; nothing to do
          return
        }
        const message = err instanceof Error ? err.message : 'Upload failed'
        setPending(prev =>
          prev.map(p =>
            p.localId === att.localId ? { ...p, status: 'failed', error: message } : p
          )
        )
        if (reason === 'too_large') {
          toast.error(`${att.file.name} is too large (max 10MB)`)
        } else {
          toast.error(`Failed to upload ${att.file.name}`)
        }
      } finally {
        activeCountRef.current -= 1
        const next = queueRef.current.shift()
        if (next) {
          // Re-claim the slot synchronously before async dispatch.
          activeCountRef.current += 1
          startUpload(next)
        }
      }
    },
    [uploadFile]
  )

  const enqueueUpload = useCallback(
    (att: PendingAttachment) => {
      if (activeCountRef.current < UPLOAD_CONCURRENCY) {
        // Claim the slot synchronously so a follow-up enqueue in the same
        // tick sees the updated count.
        activeCountRef.current += 1
        startUpload(att)
      } else {
        queueRef.current.push(att)
      }
    },
    [startUpload]
  )

  const addFiles = useCallback(
    (files: File[]) => {
      setPending(prev => {
        const remaining = MAX_CHAT_ATTACHMENTS - prev.length
        if (remaining <= 0) {
          toast.error(`You can attach up to ${MAX_CHAT_ATTACHMENTS} images per message`)
          return prev
        }

        const accepted: PendingAttachment[] = []
        let droppedForLimit = 0

        for (const file of files) {
          if (!file.type.startsWith('image/')) continue

          if (accepted.length >= remaining) {
            droppedForLimit += 1
            continue
          }

          if (file.size > MAX_BYTES) {
            toast.error(`${file.name} is too large (max 10MB)`)
            continue
          }

          accepted.push({
            localId: ulid(),
            file,
            previewUrl: URL.createObjectURL(file),
            status: 'uploading',
            abort: new AbortController(),
          })
        }

        if (droppedForLimit > 0) {
          toast.error(`Only ${remaining} more image(s) can be attached`)
        }

        for (const att of accepted) enqueueUpload(att)

        return [...prev, ...accepted]
      })
    },
    [enqueueUpload]
  )

  const removeAt = useCallback((localId: string) => {
    // Capture the target outside the setState updater so side effects
    // (abort, revokeObjectURL) fire exactly once even under StrictMode's
    // double-invoked updater.
    let target: PendingAttachment | undefined
    setPending(prev => {
      target = prev.find(p => p.localId === localId)
      return prev.filter(p => p.localId !== localId)
    })
    if (target) {
      target.abort.abort()
      URL.revokeObjectURL(target.previewUrl)
    }
    queueRef.current = queueRef.current.filter(q => q.localId !== localId)
  }, [])

  const clear = useCallback(() => {
    let toClean: PendingAttachment[] = []
    setPending(prev => {
      toClean = prev
      return []
    })
    for (const p of toClean) {
      p.abort.abort()
      URL.revokeObjectURL(p.previewUrl)
    }
    queueRef.current = []
  }, [])

  // Cleanup on unmount
  const pendingRef = useRef(pending)
  pendingRef.current = pending
  useEffect(() => {
    return () => {
      for (const p of pendingRef.current) {
        p.abort.abort()
        URL.revokeObjectURL(p.previewUrl)
      }
    }
  }, [])

  const hasUploading = pending.some(p => p.status === 'uploading')
  const ready = pending.filter(p => p.status === 'ready')

  return { pending, addFiles, removeAt, clear, hasUploading, ready }
}
